import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import { usePageNavigation } from "frontend/hooks/use-page-navigation";
import { useContentful } from "frontend/hooks/use-contentful";
import { ContentfulHead } from "frontend/contentful/components/primitives/head.contentful";
import { createSiteFooterProps } from "frontend/contentful/components/universal/site-footer.props";
import { createSiteHeaderProps } from "frontend/contentful/components/universal/site-header.props";
import { createLazyTopperProps } from "frontend/contentful/components/toppers/lazy-topper.props";
import { ArchivePageEntry } from "frontend/contentful/schema/pages";
import { SearchTopperEntry } from "frontend/contentful/schema/toppers";
import {
  ArchivePage,
  ArchivePageTopper,
} from "design-system/components/pages/archive-page/archive-page";
import { SearchTopperProps } from "design-system/components/toppers/search-topper/search-topper";
import { useDebounce } from "design-system/hooks/use-debounce";
import {
  useSearchQuery,
  SearchQueryContext,
} from "frontend/hooks/use-search-query";
import { usePageProps } from "frontend/hooks/use-page-props";
import { ContentfulPageSections } from "../page-sections.contentful";
import {
  ArchiveContentEntry,
  BentoBoxEntry,
  SearchArchiveTabsEntry,
} from "frontend/contentful/schema/semantics";
import { ArchivePageBodyProps } from "design-system/components/pages/archive-page/archive-page-body/archive-page-body";
import { createBentoBoxProps } from "../../semantics/bento-box.props";
import { createArchiveContentProps } from "../../semantics/archive-content.props";
import { BentoBox } from "design-system/sites/baker/bento-box/bento-box";
import { ArchivePageContent } from "design-system/components/search/archive-page-content/archive-page-content";
import { createSearchArchiveTabsProps } from "../../semantics/search-archive-tabs.props";
import { TabProps, Tabs } from "design-system/components/primitives/tabs/tabs";
import { SearchDropdownsProvider } from "frontend/hooks/use-search-dropdowns";
import { BentoBoxColumnProps } from "design-system/sites/baker/bento-box/bento-column";
import { useDataLayer } from "frontend/hooks/use-data-layer";

const isBodySearchArchiveTabs = (
  body?: ArchiveContentEntry | BentoBoxEntry | SearchArchiveTabsEntry,
): body is SearchArchiveTabsEntry =>
  body !== undefined && "tabs" in body.fields;
const isBodyBentoBox = (
  body?: ArchiveContentEntry | BentoBoxEntry | SearchArchiveTabsEntry,
): body is BentoBoxEntry => body !== undefined && "columns" in body.fields;

interface ContentfulArchivePageInnerProps {
  entry: ArchivePageEntry;
}

const ContentfulArchivePageInner = ({
  entry,
}: ContentfulArchivePageInnerProps) => {
  const { siteArchivePageSettings } = usePageProps();
  const { site, getEntry } = useContentful();
  const { breadcrumbs } = usePageNavigation();
  const { pushDataLayer } = useDataLayer();
  const bentoLoadedRef = useRef<boolean>(false);
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>(
    searchParams.get("q") || "",
  );

  const body = getEntry(entry.fields.body);
  let content: ArchivePageBodyProps["content"] = undefined;

  if (isBodySearchArchiveTabs(body)) {
    const props = createSearchArchiveTabsProps(body);
    if (props) {
      content = <Tabs {...props} />;
    }
  } else if (isBodyBentoBox(body)) {
    const props = createBentoBoxProps(body);
    if (props) {
      content = <BentoBox {...props} />;
    }
  } else {
    const props = createArchiveContentProps(body);
    if (props) {
      content = <ArchivePageContent {...props} />;
    }
  }

  const { handleInputKeyDown, handleMouseDown } = useSearchQuery();

  const handleSearchQueryChange = (value: string) => {
    if (value !== searchQuery) {
      setSearchQuery(value);
    }
  };

  const topper = createLazyTopperProps(
    entry.fields.topper,
  ) as ArchivePageTopper;
  if (
    isContentType<SearchTopperEntry>(
      "searchTopper",
      getEntry(entry.fields.topper),
    )
  ) {
    const extraProps: Partial<SearchTopperProps> = {
      query: searchQuery,
      onChange: handleSearchQueryChange,
      onKeyDown: handleInputKeyDown,
      onMouseDown: handleMouseDown,
    };
    topper.props = { ...topper.props, ...extraProps };
  }

  interface ArchiveTabs {
    props: {
      tabs: Array<TabProps>;
    };
  }

  interface BentoTabContent {
    tabContent: {
      props: {
        bentoBoxColumns: Array<BentoBoxColumnProps>;
      };
    };
  }

  useEffect(() => {
    if (isBodySearchArchiveTabs(body)) {
      const archiveTabContent = content as ArchiveTabs;
      let bentoColumnsLoaded = true;
      let isBento = false;
      const tabs = archiveTabContent.props.tabs;
      tabs.forEach((tab) => {
        const bentoTab: BentoTabContent = tab as BentoTabContent;
        if (bentoTab?.tabContent?.props?.bentoBoxColumns) {
          isBento = true;
          bentoTab.tabContent.props.bentoBoxColumns?.forEach((column) => {
            if (column.isLoading) {
              bentoColumnsLoaded = false;
            }
          });
        }
      });

      if (isBento && bentoColumnsLoaded && !bentoLoadedRef.current) {
        const url = new URL(window.location.href);
        const bentoBoxCategory: Array<string> = [];
        const bentoBoxNumResults: Array<string> = [];
        const type = url.href.includes("search")
          ? "search-primary"
          : "search-secondary";
        tabs.forEach((tab) => {
          const bentoTab: BentoTabContent = tab as BentoTabContent;
          bentoTab.tabContent.props.bentoBoxColumns?.forEach((column) => {
            if (
              column.isLoading === false &&
              !bentoBoxCategory.includes(column.title)
            ) {
              bentoBoxCategory.push(column.title);
              bentoBoxNumResults.push(
                column.results?.split(" ")[0]?.replaceAll(",", "") || "",
              );
            }
          });
        });

        pushDataLayer({
          /* eslint-disable camelcase */
          event: "bento_box_search_results",
          event_data: {
            search_term: url.searchParams.get("q") || "",
            search_type: type,
            search_result_page_url: window.location.href.split("?")[0],
            bento_box_category: bentoBoxCategory.join(","),
            bento_box_numresults: bentoBoxNumResults.join(","),
          },
          /* eslint-enable camelcase */
        });
      }
      if (bentoColumnsLoaded) {
        bentoLoadedRef.current = true;
      } else {
        bentoLoadedRef.current = false;
      }
    }
  }, [body, pushDataLayer]);

  return (
    <>
      <ContentfulHead entry={entry.fields.seo} />

      <ArchivePage
        theme={getEntry(siteArchivePageSettings)?.fields.colorThemeOverride}
        header={{
          name: "SiteHeader",
          props: createSiteHeaderProps(site),
        }}
        footer={createSiteFooterProps(site?.fields.footer)}
        topper={topper}
        breadcrumbs={breadcrumbs}
        body={{
          topContent: (
            <ContentfulPageSections
              entry={entry}
              options={{ key: "topContent" }}
            />
          ),
          content,
          preFooterContent: (
            <ContentfulPageSections
              entry={entry}
              options={{ key: "preFooterContent" }}
            />
          ),
        }}
      />
    </>
  );
};

export const ContentfulArchivePage =
  createContentfulComponent<ArchivePageEntry>(({ entry }) => {
    const searchParams = useSearchParams();
    const [searchQuery, setSearchQuery] = useState<string>(
      decodeURI(searchParams.get("q") || ""),
    );
    const debouncedQuery = useDebounce(searchQuery, 300);
    const prevUrl = useRef<string>();

    useEffect(() => {
      const url = new URL(window.location.href);

      if (debouncedQuery) {
        url.searchParams.set("q", encodeURI(debouncedQuery));
      } else {
        url.searchParams.delete("q");
      }

      if (prevUrl.current && prevUrl.current !== url.href) {
        window.history.pushState({}, "", url);
      }
      prevUrl.current = url.href;
    }, [debouncedQuery]);

    return (
      <SearchQueryContext.Provider
        value={{ searchQuery, setSearchQuery, debouncedQuery }}
      >
        <SearchDropdownsProvider topper={entry.fields.topper}>
          <ContentfulArchivePageInner entry={entry} />
        </SearchDropdownsProvider>
      </SearchQueryContext.Provider>
    );
  });
